import classNames from "classnames"
import { Spinner } from "components/Spinner/Spinner"
import { Tooltip } from "components/Tooltip/Tooltip"
import { TranslatableText, TranslatableTextProps } from "components/TranslatableText/TranslatableText"
import { motion } from "framer-motion"
import debounce from "lodash.debounce"
import React, { forwardRef } from "react"
import { AriaButtonProps, useId } from "react-aria"
import { trackButtonClick } from "tracking/trackButtonClick"

import {
  BUTTON_BASE_CLASSES,
  BUTTON_IMPACT_CLASSES,
  BUTTON_MIN_WIDTH_CLASSES,
  BUTTON_SHAPE_CLASSES,
  BUTTON_SIZE_CLASSES,
} from "./constants"

export type ButtonVariants = "primary" | "secondary" | "text" | "error" | "danger" | "unstyled"
export type ButtonImpacts = "bold" | "light" | "none"
export type ButtonSizes = "xs" | "sm" | "sm-light" | "md" | "md-light" | "lg" | "xl"
export type ButtonShape = "rounded" | "square" | "pill" | "rounded-left" | "rounded-right"
export type ButtonMinWidth = "none" | "xl"

export interface ButtonProps
  extends Omit<AriaButtonProps, "onClick" | "children" | "aria-label">, TranslatableTextProps {
  variant?: ButtonVariants
  impact?: ButtonImpacts
  size?: ButtonSizes
  shape?: ButtonShape
  disabled?: boolean
  loading?: boolean
  prefixIcon?: React.ReactNode
  suffixIcon?: React.ReactNode
  tooltipText?: string
  tooltipPosition?: "top" | "bottom" | "left" | "right"

  /** Accessible label for the button, applied as the `aria-label` attribute. Useful for icon-only buttons. */
  ariaLabel?: string
  form?: string
  className?: string
  badge?: number
  tabIndex?: number
  style?: React.CSSProperties
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onClickDebounce?: number
  minWidth?: ButtonMinWidth

  /**
   *  A special name that we use for tracking user activity. When the button is clicked
   *  and this property is set, we will send a tracking event to our analytics platform.
   */
  trackingName?: string

  /**
   *  Similar to `trackingName`, but this is a more specific id that we use for tracking
   *  user activity.
   */
  trackingId?: string

  /** If true, content will take full width without centering (useful for split layouts) */
  fullWidthContent?: boolean
}

/** Returns default impact for a given variant */
const getDefaultImpact = (variant: ButtonVariants): ButtonImpacts => {
  switch (variant) {
    case "primary":
      return "bold"
    case "secondary":
      return "light"
    case "text":
      return "none"
    case "danger":
      return "none"
    default:
      return "bold"
  }
}

type BtnMouseEvent = React.MouseEvent<HTMLButtonElement, MouseEvent>

/** Design system button. See the Storybook docs page for usage guidelines and examples. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      impact,
      size = "md",
      shape = "rounded",
      loading,
      prefixIcon,
      suffixIcon,
      tooltipText,
      tooltipPosition,
      ariaLabel,
      className,
      t,
      tValues,
      children,
      badge,
      onClick,
      onClickDebounce = 50,
      minWidth = "none",
      trackingName,
      trackingId,
      fullWidthContent = false,
      ...rest
    }: ButtonProps,
    ref
  ) => {
    const tooltipId = useId()
    const resolvedImpact = impact || getDefaultImpact(variant)

    // Create a debounced version of `onClick`
    const debouncedOnClick = debounce((e: BtnMouseEvent) => {
      onClick?.(e)
    }, onClickDebounce)

    // Handle `click` with an edge-case for keyboard events that are
    // transformed into a click even with polling.
    const handleClick = (e: BtnMouseEvent) => {
      // track the button click event
      trackButtonClick({ trackingId: trackingId, buttonName: trackingName })

      if (!onClick) {
        return
      }

      // For keyboard events (pointerId === -1), use a debounce. By having a
      // `debounce` the Enter key effectively becomes an `onKeyUp` event, which
      // is much simpler than trying to turn the keyboard events into
      // custom/synthetic events with special behavior as they might differ per
      // browser/OS
      if ((e.nativeEvent as PointerEvent)?.pointerId === -1) {
        debouncedOnClick?.(e)
      } else {
        // For actual mouse clicks, execute immediately
        onClick(e)
      }
    }

    const hasTextContent = t !== undefined || children !== undefined
    const content = (
      <TranslatableText t={t} tValues={tValues}>
        {children}
      </TranslatableText>
    )

    return (
      <span data-tooltip-content={tooltipText} data-tooltip-place={tooltipPosition} data-tooltip-id={tooltipId}>
        <motion.button
          className={classNames(
            variant === "unstyled" ? "relative" : BUTTON_BASE_CLASSES,
            variant === "unstyled" ? "" : BUTTON_SIZE_CLASSES[size],
            BUTTON_IMPACT_CLASSES[variant][resolvedImpact],
            BUTTON_SHAPE_CLASSES[shape],
            BUTTON_MIN_WIDTH_CLASSES[minWidth],
            className
          )}
          ref={ref}
          onClick={handleClick}
          aria-label={ariaLabel}
          {...rest}
        >
          {prefixIcon}
          {loading && (
            <>
              <Spinner className="absolute size-4" />
              <span aria-readonly="true" className="hidden">
                Loading...
              </span>
            </>
          )}
          {hasTextContent && (
            <span
              className={classNames(
                "transition",
                fullWidthContent && "flex w-full",
                loading
                  ? "opacity-0"
                  : fullWidthContent
                    ? "opacity-100"
                    : "mt-[1px] inline-flex items-center gap-2 opacity-100"
              )}
            >
              {content}
            </span>
          )}
          {suffixIcon}
          {!!badge && (
            <span className="absolute right-0 top-0 flex size-[14px] content-center items-center justify-center rounded-full bg-blue-500 text-label/regular text-white">
              {badge}
            </span>
          )}
        </motion.button>
        <Tooltip
          id={tooltipId}
          className="!z-50 !rounded-md !bg-gray-900 !px-3 !py-2 !text-body3/regular !text-white"
        />
      </span>
    )
  }
)
Button.displayName = "Button"
